import { Icons, SvgIcon } from '@bob/library'
import React, { useMemo } from 'react'
import { match } from 'ts-pattern'

import { Spinner } from '../Spinner/Spinner'
import { button } from './Button.css'

export enum BUTTON_WIDTH {
  Full = 'full',
  Auto = 'auto',
}

export type ButtonProps = {
  onClick?: (e: React.MouseEvent) => void
  type: 'primary' | 'secondary' | 'tertiary' | 'link' | 'icon'
  size: 'small' | 'medium' | 'large'
  color: 'blue' | 'black'
  colorMode: 'light' | 'dark'
  iconName?: keyof typeof Icons | 'NONE'
  iconPosition?: 'left' | 'right' //Only relevant if iconName is set
  disabled?: boolean
  width?: BUTTON_WIDTH
  target?: '_blank' | '_self'
  rel?: 'noreferrer noopener' | ''
  text?: string
  href?: string | null
  id?: string
  isLoading?: boolean
  dataTestid?: string
  download?: boolean
  removePadding?: boolean // Should only be set for button type tertiary or link
  fontSize?: 'sm' | 'md' | 'lg'
  reference?: React.MutableRefObject<HTMLAnchorElement | HTMLButtonElement | null>
  channelSightAssetId?: number | string
  channelSightProductSku?: number | string
  channelSightClassName?: string
}

export const Button: React.FC<ButtonProps> = ({
  onClick,
  type = 'primary',
  size = 'medium',
  color = 'blue',
  colorMode = 'light',
  iconPosition = 'left',
  disabled = false,
  width = BUTTON_WIDTH.Auto,
  target = '_self',
  isLoading = false,
  rel,
  text,
  iconName,
  href,
  id,
  dataTestid,
  removePadding = false,
  download = false,
  fontSize = 'md', // should be depricated
  reference,
  channelSightAssetId,
  channelSightProductSku,
  channelSightClassName,
}) => {
  const iconSize = match({ size, type })
    .with({ size: 'small', type: 'primary' }, () => 16)
    .with({ size: 'small', type: 'secondary' }, () => 16)
    .with({ size: 'small', type: 'tertiary' }, () => 16)
    .with({ size: 'small', type: 'link' }, () => 16)
    .with({ size: 'small', type: 'icon' }, () => 22)
    .with({ size: 'medium', type: 'primary' }, () => 24)
    .with({ size: 'medium', type: 'secondary' }, () => 24)
    .with({ size: 'medium', type: 'tertiary' }, () => 20)
    .with({ size: 'medium', type: 'link' }, () => 20)
    .with({ size: 'medium', type: 'icon' }, () => 22)
    .with({ size: 'large', type: 'primary' }, () => 26)
    .with({ size: 'large', type: 'secondary' }, () => 26)
    .with({ size: 'large', type: 'tertiary' }, () => 24)
    .with({ size: 'large', type: 'link' }, () => 24)
    .with({ size: 'large', type: 'icon' }, () => 24)
    .otherwise(() => 16)

  const iconStrokeWidth = match({ size, type })
    .with({ size: 'small', type: 'link' }, () => 2)
    .with({ size: 'small', type: 'icon' }, () => 3)
    .with({ size: 'medium', type: 'link' }, () => 3)
    .otherwise(() => 4)

  const elementProps = {
    className: `${button({
      color,
      size,
      type,
      colorMode,
      width,
      removePadding,
      fontSize,
    })} ${channelSightClassName}`,
    disabled: disabled,
    download: download,
    href: href ? href : undefined,
    id: id,
    onClick: onClick,
    rel: href && rel ? rel : target === '_blank' ? 'noreferrer noopener' : undefined,
    target: href ? target : undefined,
    'data-asset-id': channelSightAssetId,
    'data-product-sku': channelSightProductSku,
  }

  const elementContent = useMemo(() => {
    const iconComponent =
      iconName && iconName !== 'NONE' ? (
        <SvgIcon color={'none'} name={iconName} size={iconSize} strokeWidth={iconStrokeWidth} />
      ) : null
    return (
      <>
        {!isLoading && (
          <>
            {type === 'icon' ? (
              iconComponent
            ) : (
              <>
                {iconName && iconPosition === 'left' && iconComponent}
                {text}
                {iconName && iconPosition === 'right' && iconComponent}
              </>
            )}
          </>
        )}
        {isLoading && (
          <>
            {type === 'icon' ? (
              <Spinner height={iconSize} width={iconSize} />
            ) : (
              <>
                {(iconPosition === 'left' || !iconPosition) && (
                  <Spinner height={iconSize} width={iconSize} />
                )}
                {text}
                {iconPosition === 'right' && <Spinner height={iconSize} width={iconSize} />}
              </>
            )}
          </>
        )}
      </>
    )
  }, [iconName, iconPosition, iconSize, iconStrokeWidth, isLoading, text, type])

  if (href) {
    return (
      <a
        {...elementProps}
        aria-disabled={disabled}
        data-testid={dataTestid}
        ref={reference as React.MutableRefObject<HTMLAnchorElement | null> | undefined}
      >
        {elementContent}
      </a>
    )
  }

  return (
    <button
      {...elementProps}
      aria-disabled={disabled}
      data-testid={dataTestid}
      ref={reference as React.MutableRefObject<HTMLButtonElement | null> | undefined}
    >
      {elementContent}
    </button>
  )
}
